import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '~/design-system/foundations/Breadcrumb'
import {HgBreadcrumbOverflowMenu} from './HgBreadcrumbOverflowMenu'
import {HgTextLink, type HgTextLinkProps} from '~/design-system/hg/components'

const NUM_VISIBLE_BREADCRUMBS = 3

export type BreadcrumbLinkProps = {
  label: React.ReactNode
  link: Omit<HgTextLinkProps, 'variant' | 'children'>
}
export type HgBreadcrumbProps = {
  breadcrumbs: BreadcrumbLinkProps[]
} & React.HTMLAttributes<HTMLDivElement>

function HgBreadcrumbItem({
  breadcrumb,
  withSeparator,
}: {
  breadcrumb: BreadcrumbLinkProps
  withSeparator: boolean
}) {
  return (
    <>
      <BreadcrumbItem>
        <BreadcrumbLink
          data-analytics-event-name={breadcrumb.link.analyticsEventName}
          asChild
        >
          <HgTextLink
            className="arcadia-ui-1"
            href={breadcrumb.link.href}
            variant="underline"
          >
            {breadcrumb.label}
          </HgTextLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      {withSeparator && <BreadcrumbSeparator className="px-s2 text-icon-subdued" />}
    </>
  )
}

function HgBreadcrumbListDefault({
  breadcrumbs,
}: {
  breadcrumbs: BreadcrumbLinkProps[]
}) {
  return breadcrumbs.map((breadcrumb, index) => (
    <HgBreadcrumbItem
      key={index}
      breadcrumb={breadcrumb}
      withSeparator={index < breadcrumbs.length - 1}
    />
  ))
}

function HgBreadcrumbListOverflow({
  breadcrumbs,
}: {
  breadcrumbs: BreadcrumbLinkProps[]
}) {
  const numOverflowBreadcrumbs = breadcrumbs.length - NUM_VISIBLE_BREADCRUMBS
  return (
    <>
      <HgBreadcrumbItem breadcrumb={breadcrumbs[0]} withSeparator={true} />
      <BreadcrumbItem>
        <HgBreadcrumbOverflowMenu
          breadcrumbs={breadcrumbs.slice(1, 1 + numOverflowBreadcrumbs)}
        />
      </BreadcrumbItem>
      <BreadcrumbSeparator className="px-s2 text-icon-subdued" />
      <HgBreadcrumbListDefault
        breadcrumbs={breadcrumbs.slice(1 + numOverflowBreadcrumbs)}
      />
    </>
  )
}

export default function HgBreadcrumb({
  breadcrumbs,
  ...htmlProps
}: HgBreadcrumbProps) {
  const hasOverflow = breadcrumbs.length > NUM_VISIBLE_BREADCRUMBS
  return (
    <Breadcrumb {...htmlProps}>
      <BreadcrumbList className="flex items-center text-left text-text-subdued arcadia-ui-1">
        {hasOverflow ? (
          <HgBreadcrumbListOverflow breadcrumbs={breadcrumbs} />
        ) : (
          <HgBreadcrumbListDefault breadcrumbs={breadcrumbs} />
        )}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
